/**
 * Everything the sandbox screens decide, without React, Convex or Clerk.
 *
 * The backend half is `features/sandboxImport/` (contract:
 * `docs/SANDBOX_IMPORT.md`). It speaks in error CODES and in a single job row
 * per account; this module turns both into the three things the UI needs:
 * where the route gate sends a user, what the setup page renders, and which
 * way out a failure offers.
 */

/** The job row as the app reads it — `Doc<'sandboxJobs'>` minus its metadata. */
export interface SandboxJobView {
  kind: 'seed' | 'reset' | 'blank';
  status: 'running' | 'done' | 'failed';
  /** Position in the walk: `handshake`, `delete`, `bills (2/3)`, `done`… */
  phase: string;
  /** Rows written, per table. */
  counts: Record<string, number>;
  skipped: number;
  /** An error CODE, never a message — see `describeSandboxError`. */
  error?: string;
  startedAt: number;
  updatedAt: number;
  finishedAt?: number;
}

/** `undefined` is a query still in flight, `null` a user who never imported. */
export type MaybeJob = SandboxJobView | null | undefined;

export type SandboxGateDecision = 'loading' | 'setup' | 'onboarding' | 'allow';

/**
 * Whether a signed-in sandbox user may reach the app at all.
 *
 * Ways to be sent to `/sandbox/setup`: no account in this deployment yet, an
 * import currently running (during a reset the account is being torn down
 * under their feet), a run that failed — a half-imported account is not an
 * account, and the reset offered there is the only way out of it — and an
 * account that arrived without a usable company.
 *
 * That last one is not cosmetic. `ProtectedRoute` sends a user with no
 * company to `/onboarding`, which a sandbox build sends straight back to the
 * setup page, which would return them to the dashboard: a redirect loop with
 * no exit. Refusing here is what breaks it — and it is the honest answer, a
 * copy with nothing to show is a copy to take again.
 *
 * A finished `blank` job is the one case where an account with nothing in it
 * is not a broken copy but the whole point: the user asked for an empty
 * sandbox, so `onboarding` sends them to the wizard that builds their company
 * — the same one production uses, which is exactly why the empty sandbox is
 * not a dead end. `seed` and `reset` are untouched by this rule.
 */
export function resolveSandboxGate(args: {
  hasUserRow: boolean;
  hasCompany: boolean;
  job: MaybeJob;
}): SandboxGateDecision {
  const { hasUserRow, hasCompany, job } = args;
  if (job === undefined) return 'loading';
  if (job && job.status !== 'done') return 'setup';
  if (hasUserRow && hasCompany) return 'allow';
  return job?.kind === 'blank' ? 'onboarding' : 'setup';
}

export type SandboxSetupState =
  | { kind: 'loading' }
  | { kind: 'idle' }
  | { kind: 'running'; phase: string; imported: number; skipped: number }
  | { kind: 'failed'; code: string }
  | { kind: 'done'; imported: number; skipped: number };

/** The same fallback the backend uses when it cannot name what went wrong. */
const GENERIC_CODE = 'SANDBOX_IMPORT_FAILED';
/** Mirrors the backend takeover threshold in `sandboxImport/jobs.ts`. */
export const SANDBOX_JOB_STALE_MS = 15 * 60 * 1000;

export function resolveSetupState(
  job: MaybeJob,
  now: number = Date.now(),
): SandboxSetupState {
  if (job === undefined) return { kind: 'loading' };
  if (job === null) return { kind: 'idle' };
  if (job.status === 'failed') {
    return { kind: 'failed', code: job.error ?? GENERIC_CODE };
  }
  if (job.status === 'running' && now - job.updatedAt > SANDBOX_JOB_STALE_MS) {
    return { kind: 'failed', code: 'SANDBOX_JOB_STALE' };
  }
  const imported = sumImported(job.counts);
  if (job.status === 'done') {
    return { kind: 'done', imported, skipped: job.skipped };
  }
  return { kind: 'running', phase: job.phase, imported, skipped: job.skipped };
}

/**
 * What a failure offers next:
 * - `reset` — "Reset & retry": the account is (partly) here and running the
 *   import again is what fixes it;
 * - `signOut` — nothing to retry with: this Clerk account cannot be imported;
 * - `none` — the deployment, not the user, is what has to change.
 */
export type SandboxRecovery = 'reset' | 'signOut' | 'none';

export interface SandboxErrorInfo {
  messageKey: string;
  recovery: SandboxRecovery;
}

const ERROR_CODES: Record<string, SandboxErrorInfo> = {
  SANDBOX_NO_PROD_ACCOUNT: {
    messageKey: 'sandbox:errors.noProdAccount',
    recovery: 'signOut',
  },
  SANDBOX_PROD_ACCOUNT_NOT_ACTIVE: {
    messageKey: 'sandbox:errors.prodAccountNotActive',
    recovery: 'signOut',
  },
  SANDBOX_EXPORT_FORBIDDEN: {
    messageKey: 'sandbox:errors.forbidden',
    recovery: 'signOut',
  },
  FORBIDDEN: { messageKey: 'sandbox:errors.forbidden', recovery: 'signOut' },
  UNAUTHENTICATED: {
    messageKey: 'sandbox:errors.unauthenticated',
    recovery: 'signOut',
  },
  SANDBOX_JOB_RUNNING: {
    messageKey: 'sandbox:errors.jobRunning',
    recovery: 'none',
  },
  SANDBOX_ALREADY_SEEDED: {
    messageKey: 'sandbox:errors.alreadySeeded',
    recovery: 'reset',
  },
  SANDBOX_TICKET_EXPIRED: {
    messageKey: 'sandbox:errors.ticketExpired',
    recovery: 'reset',
  },
  SANDBOX_EXPORT_INVALID_TICKET: {
    messageKey: 'sandbox:errors.ticketExpired',
    recovery: 'reset',
  },
  SANDBOX_JOB_STALE: {
    messageKey: 'sandbox:errors.jobStale',
    recovery: 'reset',
  },
  SANDBOX_PROD_NOT_CONFIGURED: {
    messageKey: 'sandbox:errors.prodNotConfigured',
    recovery: 'none',
  },
  SANDBOX_EXPORT_UNAUTHORIZED: {
    messageKey: 'sandbox:errors.exportUnauthorized',
    recovery: 'none',
  },
  // The source verified the shared secret but not the forwarded Clerk JWT:
  // the signed-in account belongs to another Clerk instance. Signing out is
  // the only way through — no retry can change the token's issuer.
  SANDBOX_EXPORT_INVALID_TOKEN: {
    messageKey: 'sandbox:errors.invalidProdToken',
    recovery: 'signOut',
  },
  // The source deployment holds no `SANDBOX_EXPORT_SECRET`, so its export
  // routes are not registered at all: `gate()` answers `404 NOT_FOUND`, which
  // `toErrorCode` passes through verbatim. `SANDBOX_PROD_HTTP_404` is the same
  // wall reached the other way — the source answered, but not with an error
  // envelope, so `errorCode()` in `client.ts` named the status instead.
  NOT_FOUND: {
    messageKey: 'sandbox:errors.exportDisabled',
    recovery: 'none',
  },
  SANDBOX_PROD_HTTP_404: {
    messageKey: 'sandbox:errors.exportDisabled',
    recovery: 'none',
  },
  SANDBOX_ONLY: {
    messageKey: 'sandbox:errors.sandboxOnly',
    recovery: 'none',
  },
  DEV_PROD_IMPORT_NOT_CONFIGURED: {
    messageKey: 'sandbox:prodImport.errors.notConfigured',
    recovery: 'none',
  },
  DEV_PROD_IMPORT_SELF_SOURCE: {
    messageKey: 'sandbox:prodImport.errors.selfSource',
    recovery: 'none',
  },
  DEV_PROD_IMPORT_EMAIL_ALLOWLIST_REQUIRED: {
    messageKey: 'sandbox:prodImport.errors.emailAllowlistRequired',
    recovery: 'none',
  },
  DATA_IMPORT_MODE_MISCONFIGURED: {
    messageKey: 'sandbox:prodImport.errors.modeMisconfigured',
    recovery: 'none',
  },
};

/**
 * An unknown code — `SANDBOX_PROD_HTTP_502`, a code shipped by a backend newer
 * than this bundle — is treated as a hiccup: generic wording, retry offered.
 */
export function describeSandboxError(code: string | null): SandboxErrorInfo {
  return (
    (code ? ERROR_CODES[code] : undefined) ?? {
      messageKey: 'sandbox:errors.generic',
      recovery: 'reset',
    }
  );
}

/**
 * The code inside a thrown Convex error. Mirrors the backend's `toErrorCode`
 * — SCREAMING_SNAKE with at least one underscore, so `ECONNREFUSED` in a
 * network error is not mistaken for a contract code — plus the two
 * single-word codes the actions throw themselves.
 */
export function extractSandboxCode(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error);
  const single = /\b(?:FORBIDDEN|UNAUTHENTICATED)\b/.exec(message);
  if (single) return single[0];
  const match = /\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/.exec(message);
  return match ? match[0] : null;
}

export function sumImported(counts: Record<string, number>): number {
  return Object.values(counts).reduce((total, count) => total + count, 0);
}

/** Phases the worker names on its own; anything else is a step of the walk. */
const NAMED_PHASES = new Set([
  'handshake',
  'delete',
  'user',
  'finalize',
  'done',
]);

/**
 * A phase is either one of the five fixed moments — translated — or a step of
 * the walk (`bills (2/3)`, `links (1/3)`), which the backend builds from table
 * names and which goes through verbatim inside a translated frame.
 */
export function describePhase(phase: string): { key: string; item?: string } {
  if (NAMED_PHASES.has(phase)) return { key: `sandbox:phases.${phase}` };
  return { key: 'sandbox:phases.copying', item: phase };
}

/**
 * When the sandbox account last came from production, if it ever did.
 *
 * A `blank` job is `null`: it finished, but nothing was copied. Dating a
 * production copy that never happened is what the banner and the account card
 * would otherwise do.
 */
export function lastImportAt(job: MaybeJob): number | null {
  if (!job || job.status !== 'done' || job.kind === 'blank') return null;
  return job.finishedAt ?? null;
}
